import { resolveOtelloTypingKind } from './conversationOtelloTyping';

// Regola di prodotto (14/09/2026): la lista ordina per chi ha bisogno di te,
// non per chi sta parlando. Mentre Otello lavora su una conversazione di una
// inbox API (chatbot o voice) il typing e' acceso e ogni turno arriva come
// messaggio realtime: senza questo freno la riga salirebbe in testa a ogni
// battuta e scavalcherebbe le email per tutta la durata della chiamata.
//
// Il congelamento non tocca i dati vivi: anteprima, etichette e timestamp
// della conversazione restano aggiornati. Il comparatore della lista legge
// pero' un'istantanea dei soli campi di ordinamento, scattata quando il
// typing si accende (se la conversazione e' gia' in lista) o alla prima
// scrittura che la tocca (se arriva dopo: conversation.created in ritardo,
// fetch, cache). Quando il typing si spegne l'istantanea cade e la
// conversazione viene collocata una volta sola con le regole normali.
//
// Stato nello store conversations:
//   frozenPlacements[conversationId] = { snapshot: null | {...}, floatOnRelease }
// Le funzioni qui sono pure: ricevono la mappa e ne restituiscono una nuova,
// oppure null quando non c'e' nulla da cambiare (cosi' lo store non
// riassegna lo stato e il getter non riordina per niente).

// Campi letti dai comparatori di store/modules/conversations/helpers.js:
// last_activity_at (sort di default), last_message_at / last_non_activity_message
// / messages / timestamp (viste chat e priority_urgency), last_incoming_at
// (email), created_at (spareggio), priority, waiting_since e agent_last_seen_at
// (unread-first).
export const CONVERSATION_PLACEMENT_SNAPSHOT_FIELDS = Object.freeze([
  'agent_last_seen_at',
  'created_at',
  'last_activity_at',
  'last_incoming_at',
  'last_message_at',
  'last_non_activity_message',
  'priority',
  'timestamp',
  'waiting_since',
]);

const resolveFrozenPlacementKey = (conversationId = null) =>
  conversationId === null ||
  conversationId === undefined ||
  conversationId === ''
    ? null
    : String(conversationId);

// Stessa regola della card e della bolla di typing: su una inbox API un record
// di typing qualunque vuol dire "Otello sta lavorando".
export const shouldFreezeConversationPlacement = ({
  inbox = null,
  typingUsersList = [],
} = {}) => resolveOtelloTypingKind({ inbox, typingUsersList }) !== null;

export const buildConversationPlacementSnapshot = (conversation = null) => {
  if (!conversation || typeof conversation !== 'object') return null;

  const snapshot = {};
  CONVERSATION_PLACEMENT_SNAPSHOT_FIELDS.forEach((field) => {
    snapshot[field] = conversation[field];
  });
  // ADD_MESSAGE fa push sullo stesso array: il fallback del comparatore che
  // legge i messaggi deve vedere quelli di prima.
  snapshot.messages = Array.isArray(conversation.messages)
    ? conversation.messages.slice()
    : conversation.messages;
  return snapshot;
};

export const getFrozenConversationPlacement = (
  frozenPlacements = {},
  conversationId = null
) => {
  const key = resolveFrozenPlacementKey(conversationId);
  return key === null ? null : frozenPlacements?.[key] || null;
};

export const isConversationPlacementFrozen = (
  frozenPlacements = {},
  conversationId = null
) => getFrozenConversationPlacement(frozenPlacements, conversationId) !== null;

// typing_on: idempotente. Il gateway rinnova il typing ogni pochi secondi e
// un rinnovo non deve rifare l'istantanea (la riga si sposterebbe).
export const freezeConversationPlacement = ({
  conversation = null,
  conversationId = null,
  frozenPlacements = {},
} = {}) => {
  const key = resolveFrozenPlacementKey(conversationId ?? conversation?.id);
  if (key === null || frozenPlacements?.[key]) return null;

  return {
    ...frozenPlacements,
    [key]: {
      floatOnRelease: false,
      snapshot: buildConversationPlacementSnapshot(conversation),
    },
  };
};

// typing_off (o auto-off del client): la voce cade e la conversazione viene
// collocata dalle regole normali. floatOnRelease dice allo store se, con
// l'ordine delle pagine >1 congelato, la risalita rimandata va fatta adesso.
export const releaseConversationPlacement = ({
  conversationId = null,
  frozenPlacements = {},
} = {}) => {
  const key = resolveFrozenPlacementKey(conversationId);
  if (key === null || !frozenPlacements?.[key]) return null;

  const { [key]: released, ...rest } = frozenPlacements;
  return {
    floatOnRelease: Boolean(released.floatOnRelease),
    frozenPlacements: rest,
  };
};

// Istantanea pigra: la prima scrittura che tocca una conversazione congelata
// senza istantanea fissa i valori di PRIMA della scrittura. Va chiamata dalle
// mutation prima di modificare i campi di ordinamento.
export const pinFrozenConversationPlacement = ({
  conversation = null,
  frozenPlacements = {},
} = {}) => {
  const key = resolveFrozenPlacementKey(conversation?.id);
  const entry = key === null ? null : frozenPlacements?.[key];
  if (!entry || entry.snapshot) return null;

  return {
    ...frozenPlacements,
    [key]: {
      ...entry,
      snapshot: buildConversationPlacementSnapshot(conversation),
    },
  };
};

// Pagine >1: il getter non riordina e un messaggio realtime porterebbe la
// conversazione in testa esplicitamente. Da congelata la risalita si rimanda
// allo sblocco.
export const deferFrozenConversationFloat = ({
  conversationId = null,
  frozenPlacements = {},
} = {}) => {
  const key = resolveFrozenPlacementKey(conversationId);
  const entry = key === null ? null : frozenPlacements?.[key];
  if (!entry || entry.floatOnRelease) return null;

  return {
    ...frozenPlacements,
    [key]: { ...entry, floatOnRelease: true },
  };
};

// Vista di ordinamento: l'oggetto vivo con i campi del comparatore presi
// dall'istantanea. La lista restituisce sempre l'oggetto vivo.
export const resolveConversationPlacementSortView = ({
  conversation = null,
  frozenPlacements = {},
} = {}) => {
  const entry = getFrozenConversationPlacement(
    frozenPlacements,
    conversation?.id
  );
  return entry?.snapshot
    ? { ...conversation, ...entry.snapshot }
    : conversation;
};
